import { Tag, Tooltip, theme } from "antd";
import { CSSProperties } from "react";
import { getContrastTextColor } from "src/utils/color";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  UserOutlined,
  CalendarOutlined,
  BranchesOutlined,
  FileTextOutlined,
  TagOutlined,
  RocketOutlined,
  FileAddOutlined,
  BugOutlined,
  AlertOutlined,
  SettingOutlined,
  DatabaseOutlined,
  ApiOutlined,
  CodeOutlined,
  CustomerServiceOutlined,
  ExperimentOutlined,
  FormatPainterOutlined,
  ToolOutlined,
  FundProjectionScreenOutlined,
  PullRequestOutlined,
  ProjectOutlined,
  FieldTimeOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  MessageOutlined,
  CommentOutlined,
  CiCircleOutlined,
  GiftOutlined,
  DollarOutlined,
  DashboardOutlined,
  MonitorOutlined,
  ArrowUpOutlined,
  MinusOutlined,
  ArrowDownOutlined,
  DownOutlined,
  RobotOutlined,
} from "@ant-design/icons";

// Entity types supported by the component
export type EntityType =
  // Core entities
  | "issue"
  | "pr"
  | "commit"
  | "branch"
  | "repository"
  // Project management
  | "project"
  | "sprint"
  | "board"
  | "flow"
  | "team"
  // Communication
  | "channel"
  | "message"
  | "discussion"
  | "comment"
  | "issuecomment"
  | "discussioncomment"
  | "prrcomment"
  // Calendar and time tracking
  | "calendarentry"
  | "worklog"
  | "timeinstatus"
  // CI/CD and deployment
  | "cicdpipeline"
  | "cicdworkflow"
  | "artifact"
  | "release"
  // Code and files
  | "sourcefile"
  | "tag"
  // People and status
  | "person"
  | "userstatus"
  | "usercallstatus"
  // Monitoring and costs
  | "monitor"
  | "cost"
  | "costforecast"
  | "slo"
  // Other
  | "event"
  | "customer"
  | "service"
  | "incident";

// Common status values across entity types
export type StatusValue =
  | "open"
  | "in-progress"
  | "in-review"
  | "done"
  | "completed"
  | "blocked"
  | "draft"
  | "merged"
  | "closed";

// Action types for better type safety
export type ActionType =
  | "created"
  | "initialized"
  | "started" // Creation actions
  | "updated"
  | "modified"
  | "changed" // Progress actions
  | "completed"
  | "finished"
  | "resolved" // Completion actions
  | "blocked"
  | "stopped"
  | "failed" // Blocking actions
  | "reviewed"
  | "approved"
  | "commented" // Review actions
  | "system"
  | "automated"
  | "scheduled"; // System actions

// Classification types
export type ClassificationType =
  | "feature"
  | "enhancement"
  | "defect"
  | "fix" // Core development
  | "configuration"
  | "database"
  | "migration"
  | "dependency"
  | "refactor" // Technical
  | "documentation"
  | "support"
  | "feedback" // Support & Documentation
  | "testing"
  | "formatting" // Quality & Testing
  | "task"
  | "tech debt"; // Project Management

// Urgency types
export type UrgencyType = "high" | "important" | "nice to have";

// Issue type definitions
export type IssueType =
  | "task"
  | "subtask"
  | "story"
  | "bug"
  | "epic"
  | "feature"
  | "incident";

// Priority type definitions
export type PriorityType = "highest" | "high" | "medium" | "low" | "lowest";

// Update IndicatorType to include issue type and priority
export enum IndicatorType {
  status = "status",
  action = "action",
  entity = "entity",
  metadata = "metadata",
  classification = "classification",
  urgency = "urgency",
  issuetype = "issuetype",
  priority = "priority",
  date = "date",
}

// Style variants
export type StyleVariant = "fill" | "outlined";

// Size options
export type SizeType = "small" | "medium" | "large";

// Color theme definition
export interface ColorTheme {
  // Primary colors
  primary: string; // Blue - #1890ff - Main brand color
  success: string; // Green - #52c41a - Success state
  warning: string; // Gold - #faad14 - Warning state
  error: string; // Red - #f5222d - Error state
  info: string; // Cyan - #13c2c2 - Info state

  // Extended palette
  purple: string; // #722ed1 - For review states
  orange: string; // #fa8c16 - For high priority
  pink: string; // #eb2f96 - For special states
  geekblue: string; // #2f54eb - For technical items
  volcano: string; // #fa541c - For urgent items
  gray: string; // #8c8c8c - For inactive/disabled states
}

// Default theme colors (Airtable inspired - muted professional)
export const defaultTheme: ColorTheme = {
  primary: "#4573D5", // Muted Blue
  success: "#57A773", // Sage Green
  warning: "#E8B64C", // Muted Gold
  error: "#E26D69", // Soft Red
  info: "#4DACBD", // Muted Cyan
  purple: "#9D7BD8", // Soft Purple
  orange: "#E89C4C", // Muted Orange
  pink: "#D67BA8", // Dusty Pink
  geekblue: "#4573D5", // Muted Blue
  volcano: "#E26D69", // Soft Red
  gray: "#8C97A8", // Cool Gray
};

// Question Editor theme (based on our pill colors)
export const questionEditorTheme: ColorTheme = {
  primary: "#58ACF2", // Sky Blue
  success: "#97B14C", // Lime Green
  warning: "#DB9640", // Amber
  error: "#EC8786", // Soft Red
  info: "#39BAA4", // Teal
  purple: "#B394F4", // Soft Purple
  orange: "#E98D61", // Warm Orange
  pink: "#DC88C7", // Rose Pink
  geekblue: "#79A4FE", // Indigo
  volcano: "#E886A7", // Rose
  gray: "#989CFF", // Violet Gray
};

// Modern Neutral theme (Inspired by Notion, Linear)
export const modernNeutralTheme: ColorTheme = {
  primary: "#0066FF", // Vibrant Blue
  success: "#2E8B57", // Sea Green
  warning: "#FFA500", // Classic Orange
  error: "#DC3545", // Crimson Red
  info: "#00B8D9", // Ocean Blue
  purple: "#6B5B95", // Muted Purple
  orange: "#FF7F50", // Coral
  pink: "#FF69B4", // Deep Pink
  geekblue: "#4169E1", // Royal Blue
  volcano: "#FF4500", // Orange Red
  gray: "#6E7681", // Slate Gray
};

// Deep Ocean theme (Inspired by IBM Carbon, Atlassian)
export const deepOceanTheme: ColorTheme = {
  primary: "#0072CE", // IBM Blue
  success: "#24A148", // Forest Green
  warning: "#F1C21B", // Golden Yellow
  error: "#DA1E28", // Cardinal Red
  info: "#1192E8", // Cerulean Blue
  purple: "#8A3FFC", // Electric Purple
  orange: "#FF832B", // Tangerine
  pink: "#FF7EB6", // Rose Pink
  geekblue: "#4589FF", // Ultra Blue
  volcano: "#EE538B", // Magenta
  gray: "#697077", // Cool Gray
};

// Color mappings for different entity types using theme colors
const entityTypeColors: Record<EntityType, keyof ColorTheme> = {
  // Core entities
  issue: "error",
  pr: "purple",
  commit: "success",
  branch: "warning",
  repository: "info",

  // Project management
  project: "primary",
  sprint: "orange",
  board: "pink",
  flow: "geekblue",
  team: "volcano",

  // Communication
  channel: "purple",
  message: "info",
  discussion: "primary",
  comment: "success",
  issuecomment: "success",
  discussioncomment: "success",
  prrcomment: "success",

  // Calendar and time tracking
  calendarentry: "orange",
  worklog: "warning",
  timeinstatus: "purple",

  // CI/CD and deployment
  cicdpipeline: "primary",
  cicdworkflow: "primary",
  artifact: "info",
  release: "success",

  // Code and files
  sourcefile: "gray",
  tag: "warning",

  // People and status
  person: "purple",
  userstatus: "primary",
  usercallstatus: "info",

  // Monitoring and costs
  monitor: "error",
  cost: "warning",
  costforecast: "orange",
  slo: "primary",

  // Other
  event: "purple",
  customer: "success",
  service: "info",
  incident: "error",
};

// Icon mappings for different entity types
const entityTypeIcons: Record<EntityType, React.ReactNode> = {
  // Core entities
  issue: <ExclamationCircleOutlined />,
  pr: <PullRequestOutlined />,
  commit: <CodeOutlined />,
  branch: <BranchesOutlined />,
  repository: <DatabaseOutlined />,

  // Project management
  project: <ProjectOutlined />,
  sprint: <FieldTimeOutlined />,
  board: <ProjectOutlined />,
  flow: <ThunderboltOutlined />,
  team: <TeamOutlined />,

  // Communication
  channel: <MessageOutlined />,
  message: <MessageOutlined />,
  discussion: <CommentOutlined />,
  comment: <CommentOutlined />,
  issuecomment: <CommentOutlined />,
  discussioncomment: <CommentOutlined />,
  prrcomment: <CommentOutlined />,

  // Calendar and time tracking
  calendarentry: <CalendarOutlined />,
  worklog: <FieldTimeOutlined />,
  timeinstatus: <ClockCircleOutlined />,

  // CI/CD and deployment
  cicdpipeline: <CiCircleOutlined />,
  cicdworkflow: <CiCircleOutlined />,
  artifact: <GiftOutlined />,
  release: <TagOutlined />,

  // Code and files
  sourcefile: <FileTextOutlined />,
  tag: <TagOutlined />,

  // People and status
  person: <UserOutlined />,
  userstatus: <UserOutlined />,
  usercallstatus: <CustomerServiceOutlined />,

  // Monitoring and costs
  monitor: <MonitorOutlined />,
  cost: <DollarOutlined />,
  costforecast: <DollarOutlined />,
  slo: <DashboardOutlined />,

  // Other
  event: <ThunderboltOutlined />,
  customer: <UserOutlined />,
  service: <ApiOutlined />,
  incident: <AlertOutlined />,
};

// Status colors based on semantic meaning
const statusColors: Record<StatusValue, keyof ColorTheme> = {
  open: "success",
  "in-progress": "primary",
  "in-review": "purple",
  done: "success",
  completed: "success",
  blocked: "error",
  draft: "warning",
  merged: "purple",
  closed: "gray",
};

const actionIcons = (action: string): React.ReactNode => {
  const normalizedAction = action.toLowerCase();

  if (
    normalizedAction.includes("creat") ||
    normalizedAction.includes("start") ||
    normalizedAction.includes("init")
  ) {
    return <TagOutlined />;
  }
  if (
    normalizedAction.includes("updat") ||
    normalizedAction.includes("modif") ||
    normalizedAction.includes("chang")
  ) {
    return <SyncOutlined />;
  }
  if (
    normalizedAction.includes("complet") ||
    normalizedAction.includes("finish") ||
    normalizedAction.includes("resolv")
  ) {
    return <CheckCircleOutlined />;
  }
  if (
    normalizedAction.includes("block") ||
    normalizedAction.includes("stop") ||
    normalizedAction.includes("fail")
  ) {
    return <ExclamationCircleOutlined />;
  }
  if (
    normalizedAction.includes("review") ||
    normalizedAction.includes("approv") ||
    normalizedAction.includes("comment")
  ) {
    return <ClockCircleOutlined />;
  }
  if (
    normalizedAction.includes("system") ||
    normalizedAction.includes("automat") ||
    normalizedAction.includes("schedul")
  ) {
    return <RobotOutlined />;
  }

  return <TagOutlined />; // Default fallback
};

// Status icons based on semantic meaning
const statusIcons: Record<StatusValue, React.ReactNode> = {
  open: <TagOutlined />,
  "in-progress": <SyncOutlined spin />,
  "in-review": <ClockCircleOutlined />,
  done: <CheckCircleOutlined />,
  completed: <CheckCircleOutlined />,
  blocked: <ExclamationCircleOutlined />,
  draft: <FileTextOutlined />,
  merged: <BranchesOutlined />,
  closed: <CheckCircleOutlined />,
};

// Action color wheel using theme colors
const actionColorWheel: Record<ActionType, keyof ColorTheme> = {
  // Creation actions
  created: "success",
  initialized: "success",
  started: "success",

  // Progress actions
  updated: "primary",
  modified: "primary",
  changed: "primary",

  // Completion actions
  completed: "purple",
  finished: "purple",
  resolved: "purple",

  // Blocking actions
  blocked: "error",
  stopped: "error",
  failed: "error",

  // Review actions
  reviewed: "orange",
  approved: "orange",
  commented: "orange",

  // System actions
  system: "gray",
  automated: "gray",
  scheduled: "gray",
};

// Classification colors using theme colors
const classificationColors: Record<
  ClassificationType,
  { color: keyof ColorTheme; icon: React.ReactNode }
> = {
  // Core development
  feature: { color: "primary", icon: <RocketOutlined /> },
  enhancement: { color: "success", icon: <FileAddOutlined /> },
  defect: { color: "error", icon: <BugOutlined /> },
  fix: { color: "error", icon: <AlertOutlined /> },

  // Technical
  configuration: { color: "purple", icon: <SettingOutlined /> },
  database: { color: "warning", icon: <DatabaseOutlined /> },
  migration: { color: "pink", icon: <SyncOutlined /> },
  dependency: { color: "info", icon: <ApiOutlined /> },
  refactor: { color: "geekblue", icon: <CodeOutlined /> },

  // Support & Documentation
  documentation: { color: "success", icon: <FileTextOutlined /> },
  support: { color: "primary", icon: <CustomerServiceOutlined /> },
  feedback: { color: "purple", icon: <CustomerServiceOutlined /> },

  // Quality & Testing
  testing: { color: "warning", icon: <ExperimentOutlined /> },
  formatting: { color: "gray", icon: <FormatPainterOutlined /> },

  // Project Management
  task: { color: "primary", icon: <ToolOutlined /> },
  "tech debt": { color: "volcano", icon: <FundProjectionScreenOutlined /> },
};

// Urgency colors using theme colors
const urgencyColors: Record<
  UrgencyType,
  { color: keyof ColorTheme; weight: number }
> = {
  high: { color: "error", weight: 3 },
  important: { color: "orange", weight: 2 },
  "nice to have": { color: "success", weight: 1 },
};

// Issue type colors using theme colors
const issueTypeColors: Record<
  IssueType,
  { color: keyof ColorTheme; icon: React.ReactNode }
> = {
  task: { color: "primary", icon: <ToolOutlined /> },
  subtask: { color: "purple", icon: <BranchesOutlined /> },
  story: { color: "success", icon: <FileTextOutlined /> },
  bug: { color: "error", icon: <BugOutlined /> },
  epic: { color: "orange", icon: <RocketOutlined /> },
  feature: { color: "primary", icon: <RocketOutlined /> },
  incident: { color: "error", icon: <AlertOutlined /> },
};

// Priority colors using theme colors
const priorityColors: Record<
  PriorityType,
  { color: keyof ColorTheme; icon: React.ReactNode }
> = {
  highest: { color: "error", icon: <ExclamationCircleOutlined /> },
  high: { color: "orange", icon: <ArrowUpOutlined /> },
  medium: { color: "warning", icon: <MinusOutlined /> },
  low: { color: "success", icon: <ArrowDownOutlined /> },
  lowest: { color: "gray", icon: <DownOutlined /> },
};

export interface MetadataTagProps {
  metadata: string;
  type: IndicatorType;
  size?: SizeType;
  variant?: StyleVariant;
  showLabel?: boolean;
  showIcon?: boolean;
  customColors?: Record<string, string>;
  tooltip?: string;
  className?: string;
  style?: CSSProperties;
  icon?: React.ReactNode;
  theme?: Partial<ColorTheme>; // Allow custom theme colors
}

// Helper function to get color based on type and metadata
const getIndicatorColor = (
  type: IndicatorType,
  metadata: string,
  customColors?: Record<string, string>,
  theme: ColorTheme = modernNeutralTheme
): string => {
  let themeColor: keyof ColorTheme = "gray";

  switch (type) {
    case "issuetype": {
      themeColor = issueTypeColors[metadata as IssueType]?.color || "gray";
      break;
    }
    case "priority": {
      themeColor = priorityColors[metadata as PriorityType]?.color || "gray";
      break;
    }
    case "classification": {
      themeColor =
        classificationColors[metadata as ClassificationType]?.color || "gray";
      break;
    }
    case "urgency": {
      themeColor = urgencyColors[metadata as UrgencyType]?.color || "gray";
      break;
    }
    case "entity": {
      themeColor = entityTypeColors[metadata as EntityType] || "gray";
      break;
    }
    case "status": {
      themeColor = statusColors[metadata as StatusValue] || "gray";
      break;
    }
    case "action": {
      if (metadata in actionColorWheel) {
        themeColor = actionColorWheel[metadata as ActionType];
      } else if (metadata.includes("creat") || metadata.includes("start")) {
        themeColor = "success";
      } else if (metadata.includes("updat") || metadata.includes("modif")) {
        themeColor = "primary";
      } else if (metadata.includes("complet") || metadata.includes("finish")) {
        themeColor = "purple";
      } else if (metadata.includes("block") || metadata.includes("fail")) {
        themeColor = "error";
      } else if (metadata.includes("review") || metadata.includes("approv")) {
        themeColor = "orange";
      }
      break;
    }
    case "metadata": {
      return customColors?.[metadata] || theme.gray;
    }
  }

  return theme[themeColor];
};

// Helper function to get icon based on type and metadata
export const getIndicatorIcon = (
  type: IndicatorType,
  metadata: string,
  customIcon?: React.ReactNode
): React.ReactNode => {
  if (customIcon) return customIcon;

  switch (type) {
    case "issuetype": {
      return issueTypeColors[metadata as IssueType]?.icon || <TagOutlined />;
    }
    case "priority": {
      return priorityColors[metadata as PriorityType]?.icon || <TagOutlined />;
    }
    case "classification": {
      return (
        classificationColors[metadata as ClassificationType]?.icon || (
          <TagOutlined />
        )
      );
    }
    case "urgency": {
      return <ExclamationCircleOutlined />;
    }
    case "entity": {
      return entityTypeIcons[metadata as EntityType] || <TagOutlined />;
    }
    case "status": {
      return statusIcons[metadata as StatusValue] || <TagOutlined />;
    }
    case "action": {
      return actionIcons(metadata as ActionType) || <TagOutlined />;
    }
    case "date": {
      return <CalendarOutlined />;
    }
    default: {
      //lets try one more thing.  lets see if we can find the indicator type by
      //looking at the key.

      return <TagOutlined />;
    }
  }
};

export const MetadataTag: React.FC<MetadataTagProps> = ({
  metadata,
  type,
  size = "medium",
  variant = "fill",
  showLabel = true,
  showIcon = true,
  customColors,
  tooltip,
  className,
  style,
  icon,
  theme: customTheme,
}) => {
  const { token } = theme.useToken();
  const normalizedMetadata = metadata?.toLowerCase();

  // Merge custom theme with modernNeutralTheme as default
  const activeTheme = { ...defaultTheme, ...customTheme };

  // Get color and icon
  const tagColor = getIndicatorColor(
    type,
    normalizedMetadata,
    customColors,
    activeTheme
  );
  const tagIcon = getIndicatorIcon(type, normalizedMetadata, icon);

  // Calculate text color based on background
  const textColor =
    variant === "fill" ? getContrastTextColor(tagColor) : tagColor;

  // Size configurations
  const sizeStyles = {
    small: { fontSize: 12, padding: "0 8px", height: 24 },
    medium: { fontSize: 14, padding: "2px 12px", height: 32 },
    large: { fontSize: 16, padding: "4px 16px", height: 40 },
  };

  const indicator = (
    <Tag
      className={className}
      style={{
        backgroundColor: variant === "fill" ? tagColor : `${tagColor}1A`,
        color: textColor,
        border: variant === "outlined" ? `1px solid ${tagColor}` : "none",
        borderRadius: token.borderRadiusLG,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        ...sizeStyles[size],
        ...style,
      }}
    >
      {showIcon && tagIcon}
      {showLabel && metadata}
    </Tag>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{indicator}</Tooltip>;
  }

  return indicator;
};
